import { useMemo, useState } from 'react';

import { formatAxisValue, formatMetricValue, type MetricFormat } from '@/lib/metric-format';

import { ChartFrame, ChartLegend } from './chart-frame';
import {
  buildGeometry,
  chartColor,
  formatAxisLabel,
  labelIndexes,
  shortenLabel,
  toFinite,
  type ChartSeries,
} from './chart-utils';

const WIDTH = 720;
const HEIGHT_POR_DEFECTO = 240;

interface BarChartProps {
  title?: string;
  labels: (string | number)[];
  series: ChartSeries[];
  format?: MetricFormat;
  /** Apila las series en una sola barra por categoría. */
  stacked?: boolean;
  /** Alto del área de dibujo; súbelo en tarjetas anchas para que no se aplaste. */
  height?: number;
}

/**
 * Gráfico de barras SVG: agrupado o apilado, con soporte de valores negativos.
 * Sin dependencias externas y con los colores de la marca.
 */
export function BarChart({
  title,
  labels,
  series,
  format = 'number',
  stacked = false,
  height = HEIGHT_POR_DEFECTO,
}: BarChartProps) {
  const [hover, setHover] = useState<number | null>(null);

  /** Para barras apiladas la escala debe considerar los totales, no cada serie. */
  const extentSeries = useMemo<ChartSeries[]>(() => {
    if (!stacked) return series;

    const totals = labels.map((_, index) =>
      series.reduce((sum, item) => sum + (toFinite(item.data[index]) ?? 0), 0),
    );

    return [{ label: 'total', data: totals }];
  }, [labels, series, stacked]);

  const geometry = useMemo(
    () => buildGeometry({ labelCount: labels.length, series: extentSeries, width: WIDTH, height }),
    [extentSeries, labels.length, height],
  );

  /** Rectángulos por serie y categoría. */
  const bars = useMemo(() => {
    const positive = new Array<number>(labels.length).fill(0);
    const negative = new Array<number>(labels.length).fill(0);

    const groupWidth = Math.min(geometry.bandWidth * 0.72, 40);
    const barWidth = stacked ? groupWidth : groupWidth / Math.max(series.length, 1);

    return series.map((item, seriesIndex) => {
      const color = item.color ?? chartColor(seriesIndex);

      const rects = labels.map((_, index) => {
        const value = toFinite(item.data[index]);
        if (value === null || value === 0) return null;

        let base = 0;

        if (stacked) {
          base = value > 0 ? (positive[index] ?? 0) : (negative[index] ?? 0);
          if (value > 0) positive[index] = base + value;
          else negative[index] = base + value;
        }

        const center = geometry.bandCenter(index);
        const offset = stacked ? 0 : -groupWidth / 2 + barWidth * seriesIndex + barWidth / 2;
        const top = geometry.valueY(Math.max(base + value, base));
        const bottom = geometry.valueY(Math.min(base + value, base));

        return {
          x: center + offset - barWidth / 2,
          y: top,
          width: Math.max(barWidth - (stacked ? 0 : 2), 1),
          height: Math.max(bottom - top, 1),
        };
      });

      return { label: item.label, color, rects, data: item.data };
    });
  }, [geometry, labels, series, stacked]);

  const visibleLabels = labelIndexes(labels.length, 8);
  const legendItems = series.map((item, index) => ({
    label: item.label,
    color: item.color ?? chartColor(index),
  }));

  return (
    <ChartFrame
      title={title}
      legend={legendItems.length > 1 ? <ChartLegend items={legendItems} /> : null}
    >
      <svg
        viewBox={`0 0 ${WIDTH} ${height}`}
        role="img"
        aria-label={title ?? 'Gráfico de barras'}
        className="h-auto w-full"
      >
        {/* Rejilla y eje Y */}
        {geometry.ticks.map((tick) => (
          <g key={tick.value}>
            <line
              x1={geometry.padding.left}
              x2={WIDTH - geometry.padding.right}
              y1={tick.y}
              y2={tick.y}
              stroke="#e6edf1"
              strokeDasharray={tick.value === 0 ? undefined : '3 4'}
            />
            <text
              x={geometry.padding.left - 8}
              y={tick.y + 3.5}
              textAnchor="end"
              className="fill-ink-400 text-[10px]"
            >
              {formatAxisValue(tick.value, format)}
            </text>
          </g>
        ))}

        {/* Etiquetas del eje X */}
        {visibleLabels.map((index) => (
          <text
            key={index}
            x={geometry.bandCenter(index)}
            y={height - 10}
            textAnchor="middle"
            className="fill-ink-400 text-[10px]"
          >
            {formatAxisLabel(labels[index])}
          </text>
        ))}

        {/* Zonas de interacción */}
        {labels.map((label, index) => (
          <g
            key={`band-${String(label)}-${index}`}
            onMouseEnter={() => setHover(index)}
            onMouseLeave={() => setHover(null)}
          >
            <rect
              x={geometry.padding.left + geometry.bandWidth * index}
              y={geometry.padding.top}
              width={geometry.bandWidth}
              height={geometry.innerHeight}
              fill={hover === index ? '#f4f7f9' : 'transparent'}
            />
          </g>
        ))}

        {/* Barras */}
        {bars.map((item, seriesIndex) => (
          <g key={`${item.label}-${seriesIndex}`}>
            {item.rects.map((rect, index) =>
              rect ? (
                <rect
                  key={`${item.label}-bar-${index}`}
                  x={rect.x}
                  y={rect.y}
                  width={rect.width}
                  height={rect.height}
                  fill={item.color}
                  rx={3}
                  opacity={hover === null || hover === index ? 1 : 0.55}
                  className="transition-opacity duration-150"
                />
              ) : null,
            )}
          </g>
        ))}
      </svg>

      {/* Tooltip */}
      {hover !== null && labels[hover] !== undefined ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-ink-50 px-2.5 py-1.5 text-[11px]">
          <span className="font-semibold text-ink-700">{shortenLabel(labels[hover], 22)}</span>

          {series.map((item, index) => (
            <span key={item.label} className="flex items-center gap-1.5 text-ink-600">
              <span
                aria-hidden="true"
                className="size-2 rounded-full"
                style={{ backgroundColor: item.color ?? chartColor(index) }}
              />
              {item.label}:{' '}
              <strong className="font-mono font-semibold text-ink-800">
                {formatMetricValue(toFinite(item.data[hover]) ?? 0, format)}
              </strong>
            </span>
          ))}
        </div>
      ) : null}
    </ChartFrame>
  );
}
